import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  TablePagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PrintIcon from '@mui/icons-material/Print';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import BlockIcon from '@mui/icons-material/Block';
import DescriptionIcon from '@mui/icons-material/Description';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL, getAuthHeaders, getCustomerId, getStoredUser } from '../auth';
import { EmptyPane, ErrorBanner, LoadingPane, PageHeader } from '../components/page-elements';

const renderValue = (value, propKey = null, fallback = 'N/A') => {
  if (!value) return fallback;
  if (typeof value === 'object') return value[propKey] || value.fullName || fallback;
  return value;
};
const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return 'N/A';
  return dt.toLocaleString();
};
const toDateKey = (value) => {
  if (!value) return '';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const year = dt.getFullYear();
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const formatDateFilterLabel = (value) => {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return '';
  const [year, month, day] = value.split('-');
  return `${Number(month)}/${Number(day)}/${year}`;
};

const getOrderStatusChipProps = (status) => {
  switch (status) {
    case 'Draft':
      return { color: 'warning', variant: 'filled' };
    case 'Submitted':
      return { color: 'info', variant: 'filled' };
    case 'Shipped':
    case 'Completed':
      return { color: 'success', variant: 'filled' };
    case 'Cancelled':
      return { color: 'error', variant: 'outlined' };
    default:
      return { color: 'default', variant: 'filled' };
  }
};

const renderPreferredRailcars = (order) => {
  if (order?.inventorySource === 'ground') {
    return 'Ground Inventory';
  }

  const primary = order?.railcarID || 'Determined by Site';
  const secondary = order?.secondaryRailcarID || 'Determined by Site';
  if (!order?.splitLoad) return primary;
  return `${primary} + ${secondary}`;
};

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('orderDate');
  const [sortDirection, setSortDirection] = useState('desc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const navigate = useNavigate();
  const location = useLocation();
  const user = useMemo(() => getStoredUser(), []);
  const statusFilter = useMemo(() => new URLSearchParams(location.search).get('status'), [location.search]);
  const dateFilter = useMemo(() => new URLSearchParams(location.search).get('date'), [location.search]);
  const customerId = getCustomerId(user);
  const isCustomer = user?.role === 'customer';
  const isInternal = user?.role === 'internal' || user?.role === 'admin';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dateFilterLabel = useMemo(
    () => formatDateFilterLabel(dateFilter),
    [dateFilter]
  );

  const fetchOrders = useCallback(() => {
    const params = new URLSearchParams();
    if (isCustomer && customerId) params.set('customerId', customerId);

    return fetch(`${API_BASE_URL}/api/orders${params.toString() ? `?${params.toString()}` : ''}`, {
      headers: getAuthHeaders(),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.json();
      })
      .then((data) => setOrders(data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [isCustomer, customerId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filteredOrders = useMemo(() => {
    let scoped = orders;

    if (statusFilter === 'open') {
      scoped = scoped.filter((order) => (order.orderStatus || 'Draft') === 'Submitted');
    } else if (statusFilter === 'shipped') {
      scoped = scoped.filter((order) => (order.orderStatus || 'Draft') === 'Shipped');
    }

    if (dateFilter) {
      scoped = scoped.filter((order) => {
        const orderDateKey = toDateKey(
          order.pickUpDate || order.orderDate || order.deliveryDate || order.createdAt
        );
        return orderDateKey === dateFilter;
      });
    }

    return scoped;
  }, [orders, statusFilter, dateFilter]);

  const getSortValue = (order, key) => {
    switch (key) {
      case 'orderNumber':
        return order.orderNumber || '';
      case 'customerName':
        return renderValue(order.customerName, 'customerName', '');
      case 'orderDate':
        return order.orderDate ? new Date(order.orderDate).getTime() : 0;
      case 'pickUpDate':
        return order.pickUpDate ? new Date(order.pickUpDate).getTime() : 0;
      case 'receiverName':
        return renderValue(order.receiverName, 'receiverName', '');
      case 'projectName':
        return renderValue(order.projectName, 'projectName', '');
      case 'materialName':
        return renderValue(order.materialName, 'materialName', '');
      case 'railcarID':
        return renderPreferredRailcars(order);
      case 'orderStatus':
        return order.orderStatus || 'Draft';
      default:
        return '';
    }
  };

  const searchedOrders = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return filteredOrders;

    return filteredOrders.filter((order) => {
      const haystack = [
        order.orderNumber,
        renderValue(order.customerName, 'customerName', ''),
        renderValue(order.receiverName, 'receiverName', ''),
        renderValue(order.projectName, 'projectName', ''),
        renderValue(order.materialName, 'materialName', ''),
        renderPreferredRailcars(order),
        order.orderStatus || 'Draft',
        formatDateTime(order.orderDate),
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');

      return haystack.includes(term);
    });
  }, [filteredOrders, search]);

  const sortedOrders = useMemo(() => {
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...searchedOrders].sort((a, b) => {
      const aVal = getSortValue(a, sortBy);
      const bVal = getSortValue(b, sortBy);

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * dir;
      }

      return String(aVal).localeCompare(String(bVal), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * dir;
    });
  }, [searchedOrders, sortBy, sortDirection]);

  const pagedOrders = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedOrders.slice(start, start + rowsPerPage);
  }, [sortedOrders, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [search, statusFilter, dateFilter, sortBy, sortDirection, rowsPerPage, orders.length]);

  const handleSort = (columnKey) => {
    if (sortBy === columnKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(columnKey);
    setSortDirection('asc');
  };

  const handleCreateBOL = (order) => navigate('/bols/create', { state: { order } });
  const handleEditDraftOrder = (order) => navigate(`/orders/create?orderId=${order._id}`);
  const handleViewOrder = (order) => navigate(`/orders/${order._id}`);
  const handleCancelOrder = (order) => {
    const confirmed = window.confirm(
      `Are you sure you want to cancel order ${order.orderNumber || ''}?`
    );
    if (!confirmed) return;
    updateOrderStatus(order._id, 'Cancelled');
  };

  const updateOrderStatus = async (orderId, orderStatus) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${orderId}`, {
        method: 'PUT',
        headers: getAuthHeaders(true),
        body: JSON.stringify({ orderStatus }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err?.message || `Failed to update order status to ${orderStatus}`);
      }
      await fetchOrders();
    } catch (err) {
      setError(err.message);
    }
  };

  const handlePrintOrder = (order) => {
    const clean = (v) =>
      String(v == null ? '' : v)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const customer = order.customerName || {};
    const material = order.materialName || {};
    const createdBy = order.createdBy || {};
    const isSplitLoad = Boolean(order.splitLoad);
    const isGround = order.inventorySource === 'ground';
    const primaryRailcar = isGround ? 'Ground Inventory' : (order.railcarID || 'Determined by Site');
    const secondaryRailcar = isGround ? 'Ground Inventory' : (order.secondaryRailcarID || 'Determined by Site');
    const locationAddress = (order.projectName || {}).fullAddress || '';
    const createdByName = createdBy.fullName
      || [createdBy.firstName, createdBy.lastName].filter(Boolean).join(' ')
      || 'N/A';

    const printContent = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Order ${clean(order.orderNumber || 'N/A')}</title>
          <style>
            @page { size: Letter; margin: 0.5in; }
            * { box-sizing: border-box; }
            body { font-family: Arial, sans-serif; margin: 0; color: #1f2937; font-size: 12px; line-height: 1.3; }
            .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; border-bottom: 2px solid #1f2937; padding-bottom: 8px; }
            .title-wrap h2 { margin: 0 0 3px 0; font-size: 20px; }
            .meta { font-size: 11px; color: #4b5563; margin: 2px 0; }
            .logo { max-height: 52px; max-width: 170px; object-fit: contain; }
            .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 10px; }
            .card { border: 1px solid #d1d5db; border-radius: 6px; padding: 8px 10px; }
            .card h3 { margin: 0 0 5px 0; font-size: 12px; font-weight: 700; color: #111827; border-bottom: 1px solid #e5e7eb; padding-bottom: 3px; }
            .card p { margin: 3px 0; font-size: 11px; }
            .section-title { font-size: 12px; font-weight: 700; color: #111827; margin: 10px 0 4px 0; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
            th, td { border: 1px solid #d1d5db; padding: 5px 8px; text-align: left; font-size: 11px; }
            th { background: #f9fafb; width: 35%; font-weight: 600; }
            .notes-box { border: 1px solid #d1d5db; border-radius: 6px; padding: 8px 10px; font-size: 11px; line-height: 1.5; white-space: pre-wrap; margin-bottom: 10px; }
            .footer { margin-top: 14px; font-size: 10px; color: #6b7280; border-top: 1px solid #e5e7eb; padding-top: 6px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title-wrap">
              <h2>Order ${clean(order.orderNumber || 'N/A')}</h2>
              <div class="meta">Order Date: ${clean(formatDateTime(order.orderDate))}</div>
              <div class="meta">Status: ${clean(order.orderStatus || 'Draft')}</div>
            </div>
            ${customer.customerLogo ? `<img class="logo" src="${clean(customer.customerLogo)}" alt="Customer Logo" />` : ''}
          </div>

          <div class="grid">
            <div class="card">
              <h3>Customer</h3>
              <p><strong>Name:</strong> ${clean(renderValue(order.customerName, 'customerName'))}</p>
              <p><strong>Shipper:</strong> ${clean(renderValue(order.shipperName, 'shipperName'))}</p>
            </div>
            <div class="card">
              <h3>Receiver / Delivery</h3>
              <p><strong>Receiver:</strong> ${clean(renderValue(order.receiverName, 'receiverName'))}</p>
              <p><strong>Location:</strong> ${clean(renderValue(order.projectName, 'projectName'))}</p>
              ${locationAddress ? `<p><strong>Address:</strong> ${clean(locationAddress)}</p>` : ''}
            </div>
          </div>

          <div class="section-title">Order Details</div>
          <table>
            <tr><th>Material</th><td>${clean(renderValue(order.materialName, 'materialName'))}${material.refNum ? ` &mdash; Ref: ${clean(material.refNum)}` : ''}</td></tr>
            ${material.truckType ? `<tr><th>Truck Type</th><td>${clean(material.truckType)}</td></tr>` : ''}
            <tr><th>Inventory Source</th><td>${clean(isGround ? 'Ground Inventory' : 'Railcar')}</td></tr>
            <tr><th>Split Load</th><td>${isSplitLoad ? 'Yes' : 'No'}</td></tr>
            <tr><th>Preferred Railcar</th><td>${clean(primaryRailcar)}</td></tr>
            ${isSplitLoad ? `<tr><th>Secondary Railcar</th><td>${clean(secondaryRailcar)}</td></tr>` : ''}
          </table>

          <div class="section-title">Schedule</div>
          <table>
            <tr><th>Pick Up Date</th><td>${clean(formatDateTime(order.pickUpDate))}</td></tr>
            <tr><th>Delivery Date</th><td>${clean(formatDateTime(order.deliveryDate))}</td></tr>
          </table>

          ${order.accessCode ? `
          <div class="section-title">Site Access</div>
          <table><tr><th>Access Code</th><td>${clean(order.accessCode)}</td></tr></table>
          ` : ''}

          ${order.notes ? `
          <div class="section-title">Notes</div>
          <div class="notes-box">${clean(order.notes)}</div>
          ` : ''}

          <div class="footer">
            Created by ${clean(createdByName)} &nbsp;&middot;&nbsp; Printed ${clean(new Date().toLocaleString())}
          </div>
        </body>
      </html>`;

    const win = window.open('', '', 'width=800,height=600');
    if (!win) return;
    win.document.write(printContent);
    win.document.close();

    const triggerPrint = () => {
      win.focus();
      win.print();
      setTimeout(() => win.close(), 300);
    };

    const images = Array.from(win.document.images || []);
    if (images.length === 0) {
      triggerPrint();
      return;
    }

    let doneCount = 0;
    const done = () => { doneCount += 1; if (doneCount >= images.length) triggerPrint(); };
    images.forEach((img) => { if (img.complete) { done(); } else { img.onload = done; img.onerror = done; } });
    setTimeout(() => { if (doneCount < images.length) triggerPrint(); }, 1200);
  };

  if (loading) return <LoadingPane label="Loading orders..." />;

  return (
    <Box p={2}>
      <PageHeader
        title={isCustomer ? 'Orders' : 'Truck Orders'}
        subtitle={
          dateFilterLabel
            ? `Track draft, submitted, and shipped orders for ${dateFilterLabel}.`
            : 'Track draft, submitted, and shipped orders in one queue.'
        }
        actions={
          isCustomer ? [<Button key="new" variant="contained" onClick={() => navigate('/orders/create')}>Create New Order</Button>] : null
        }
      />
      <ErrorBanner message={error} />
      <Box sx={{ mb: 1.25 }}>
        <TextField
          size="small"
          fullWidth
          label="Search orders"
          placeholder="Order #, customer, receiver, location, material, railcar, status"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      {sortedOrders.length === 0 ? (
        <EmptyPane
          title="No orders found"
          subtitle="Create your first order to begin scheduling unloads."
          action={isCustomer ? { label: 'Create Order', onClick: () => navigate('/orders/create') } : null}
        />
      ) : isMobile ? (
        <Stack spacing={1.2}>
          {pagedOrders.map((order) => (
            <Card
              key={order._id}
              variant="outlined"
              className="surface-hover"
              sx={{ borderRadius: 2, cursor: 'pointer' }}
              onClick={() => handleViewOrder(order)}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1" fontWeight={700}>{order.orderNumber}</Typography>
                  <Chip label={order.orderStatus || 'Draft'} size="small" {...getOrderStatusChipProps(order.orderStatus || 'Draft')} />
                </Stack>
                <Typography variant="body2" color="text.secondary">Receiver: {renderValue(order.receiverName, 'receiverName')}</Typography>
                <Typography variant="body2" color="text.secondary">Location: {renderValue(order.projectName, 'projectName')}</Typography>
                <Typography variant="body2" color="text.secondary">Material: {renderValue(order.materialName, 'materialName')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Preferred Railcar: {renderPreferredRailcars(order)}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  <Tooltip title="Print Worksheet"><IconButton color="info" onClick={(e) => { e.stopPropagation(); handlePrintOrder(order); }}><PrintIcon /></IconButton></Tooltip>
                  {isCustomer && order.orderStatus === 'Draft' && (
                    <>
                      <Tooltip title="Edit Draft"><IconButton color="warning" onClick={(e) => { e.stopPropagation(); handleEditDraftOrder(order); }}><EditIcon /></IconButton></Tooltip>
                      <Tooltip title="Submit Order"><IconButton color="success" onClick={(e) => { e.stopPropagation(); updateOrderStatus(order._id, 'Submitted'); }}><SendIcon /></IconButton></Tooltip>
                      <Tooltip title="Cancel Order"><IconButton color="error" onClick={(e) => { e.stopPropagation(); handleCancelOrder(order); }}><BlockIcon /></IconButton></Tooltip>
                    </>
                  )}
                  {isCustomer && order.orderStatus === 'Submitted' && (
                    <>
                      <Tooltip title="Edit Submitted"><IconButton color="warning" onClick={(e) => { e.stopPropagation(); handleEditDraftOrder(order); }}><EditIcon /></IconButton></Tooltip>
                      <Tooltip title="Cancel Order"><IconButton color="error" onClick={(e) => { e.stopPropagation(); handleCancelOrder(order); }}><BlockIcon /></IconButton></Tooltip>
                    </>
                  )}
                  {isInternal && order.orderStatus === 'Submitted' && (
                    <Tooltip title="Create BOL Draft"><IconButton color="primary" onClick={(e) => { e.stopPropagation(); handleCreateBOL(order); }}><DescriptionIcon /></IconButton></Tooltip>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <TableContainer component={Paper} className="surface-hover" sx={{ borderRadius: 2, border: '1px solid var(--ui-border)' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#ffffff' }}>
                <TableCell>
                  <TableSortLabel active={sortBy === 'orderNumber'} direction={sortBy === 'orderNumber' ? sortDirection : 'asc'} onClick={() => handleSort('orderNumber')}>
                    Order Number
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'orderDate'} direction={sortBy === 'orderDate' ? sortDirection : 'asc'} onClick={() => handleSort('orderDate')}>
                    Order Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'pickUpDate'} direction={sortBy === 'pickUpDate' ? sortDirection : 'asc'} onClick={() => handleSort('pickUpDate')}>
                    Pick Up Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'customerName'} direction={sortBy === 'customerName' ? sortDirection : 'asc'} onClick={() => handleSort('customerName')}>
                    Customer
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'receiverName'} direction={sortBy === 'receiverName' ? sortDirection : 'asc'} onClick={() => handleSort('receiverName')}>
                    Receiver
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'projectName'} direction={sortBy === 'projectName' ? sortDirection : 'asc'} onClick={() => handleSort('projectName')}>
                    Location
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'materialName'} direction={sortBy === 'materialName' ? sortDirection : 'asc'} onClick={() => handleSort('materialName')}>
                    Material
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'railcarID'} direction={sortBy === 'railcarID' ? sortDirection : 'asc'} onClick={() => handleSort('railcarID')}>
                    Preferred Railcar
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortBy === 'orderStatus'} direction={sortBy === 'orderStatus' ? sortDirection : 'asc'} onClick={() => handleSort('orderStatus')}>
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedOrders.map((order) => (
                <TableRow key={order._id} hover sx={{ cursor: 'pointer' }} onClick={() => handleViewOrder(order)}>
                  <TableCell>{order.orderNumber}</TableCell>
                  <TableCell>{formatDateTime(order.orderDate)}</TableCell>
                  <TableCell>{formatDateTime(order.pickUpDate)}</TableCell>
                  <TableCell>{renderValue(order.customerName, 'customerName')}</TableCell>
                  <TableCell>{renderValue(order.receiverName, 'receiverName')}</TableCell>
                  <TableCell>{renderValue(order.projectName, 'projectName')}</TableCell>
                  <TableCell>{renderValue(order.materialName, 'materialName')}</TableCell>
                  <TableCell>{renderPreferredRailcars(order)}</TableCell>
                  <TableCell><Chip label={order.orderStatus || 'Draft'} size="small" {...getOrderStatusChipProps(order.orderStatus || 'Draft')} /></TableCell>
                  <TableCell align="right">
                    <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                      <Tooltip title="Print Worksheet"><IconButton color="info" onClick={(e) => { e.stopPropagation(); handlePrintOrder(order); }}><PrintIcon /></IconButton></Tooltip>
                      {isCustomer && order.orderStatus === 'Draft' && (
                        <>
                          <Tooltip title="Edit Draft"><IconButton color="warning" onClick={(e) => { e.stopPropagation(); handleEditDraftOrder(order); }}><EditIcon /></IconButton></Tooltip>
                          <Tooltip title="Submit Order"><IconButton color="success" onClick={(e) => { e.stopPropagation(); updateOrderStatus(order._id, 'Submitted'); }}><SendIcon /></IconButton></Tooltip>
                          <Tooltip title="Cancel Order"><IconButton color="error" onClick={(e) => { e.stopPropagation(); handleCancelOrder(order); }}><BlockIcon /></IconButton></Tooltip>
                        </>
                      )}
                      {isCustomer && order.orderStatus === 'Submitted' && (
                        <>
                          <Tooltip title="Edit Submitted"><IconButton color="warning" onClick={(e) => { e.stopPropagation(); handleEditDraftOrder(order); }}><EditIcon /></IconButton></Tooltip>
                          <Tooltip title="Cancel Order"><IconButton color="error" onClick={(e) => { e.stopPropagation(); handleCancelOrder(order); }}><BlockIcon /></IconButton></Tooltip>
                        </>
                      )}
                      {isInternal && order.orderStatus === 'Submitted' && (
                        <Tooltip title="Create BOL Draft"><IconButton color="primary" onClick={(e) => { e.stopPropagation(); handleCreateBOL(order); }}><DescriptionIcon /></IconButton></Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={sortedOrders.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </TableContainer>
      )}
      {isMobile && sortedOrders.length > rowsPerPage ? (
        <TablePagination
          component="div"
          count={sortedOrders.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      ) : null}
    </Box>
  );
};

export default Orders;

